import { action, comparer, computed, makeObservable, observable } from 'mobx'
import { computedRouteParam } from '@stores/computedRouteParam'
import { ComputedRouteParam } from '@avantstay/mobx-routing-utils'
import { computedFn } from 'mobx-utils'

import {
  ExtendedColumnKey,
  FilterConfig,
  FiltersForPreview,
} from '@new/__global/components/FiltersFramework/_types'
import { EXTENDED_COLUMNS_KEY } from '@new/__global/components/FiltersFramework/_utils'

import { getLocalStorageItem, setLocalStorageItem } from '@utils/localStorage'

export interface SidePanelFiltersOverride {
  /** Shown in the panel even when the shared config keeps them off it. */
  include?: string[]
  /** Hidden from the panel even when the shared config puts them on it. */
  exclude?: string[]
}

export class FilterStore {
  computedRouteParams = {} as Record<string, ComputedRouteParam<any>>

  extendedColumnsView?: ExtendedColumnKey[] = getLocalStorageItem(EXTENDED_COLUMNS_KEY) as
    | ExtendedColumnKey[]
    | undefined

  filtersConfig: FilterConfig<any>[]

  /**
   * Per-screen side panel declaration. A screen reusing a shared filters config
   * says which filters it wants in the panel here, so the config itself never
   * needs to know about its consumers.
   */
  sidePanelFiltersOverride?: SidePanelFiltersOverride

  constructor(filtersConfig: FilterConfig<any>[], urlPatterns: string[]) {
    makeObservable<FilterStore, 'updateExtendedColumns'>(this, {
      computedRouteParams: observable,
      extendedColumnsView: observable,
      topBarFilters: computed,
      sidePanelFilters: computed,
      sidePanelFiltersOverride: observable.ref,
      setSidePanelFilters: action.bound,
      columnFilters: computed,
      activeFilters: computed,
      allFilterArgs: computed({ equals: comparer.structural }),
      filteringArgs: computed({ equals: comparer.structural }),
      activeSidePanelFilters: computed,
      activeSidePanelFiltersCount: computed,
      filtersForPreview: computed,
      sidebarFiltersCount: computed,
      setFilterValue: action.bound,
      applyFilters: action.bound,
      clearSidebarFilters: action.bound,
      clearFilter: action.bound,
      clearAllFilters: action.bound,
      updateExtendedColumns: action.bound,
      setExtendedColumnsConfig: action.bound,
      setExtendedMultipleColumnsConfig: action.bound,
    })

    this.filtersConfig = filtersConfig

    /*
    Changed from:
    `this.computedRouteParams = filtersConfig.reduce((params, currentFilter) => {...}`
    because of mobx error in storybook:
    Passing a 'computed' as initial property value is no longer supported by extendObservable. Use a getter or decorator instead.
    Ref: https://github.com/mobxjs/mobx/issues/1572
     */
    filtersConfig.forEach(currentFilter => {
      Object.defineProperty(this.computedRouteParams, currentFilter.id, {
        get() {
          return computedRouteParam(currentFilter.queryParamName, {
            patterns: urlPatterns,
            defaultValue: currentFilter.defaultValue,
            parse: currentFilter.parser,
            format: currentFilter.formatter,
          })
        },
      })
    })
  }

  get topBarFilters() {
    return this.filtersConfig.filter(({ showOnTheTopFiltersBar }) => showOnTheTopFiltersBar())
  }

  /** Called on mount by a screen with its own panel needs; reset it on unmount. */
  setSidePanelFilters(override?: SidePanelFiltersOverride) {
    this.sidePanelFiltersOverride = override
  }

  get sidePanelFilters() {
    const { include, exclude } = this.sidePanelFiltersOverride ?? {}
    return this.filtersConfig.filter(({ id, showOnTheTopFiltersBar, showOnTheSidePanel }) => {
      if (exclude?.includes(id)) return false
      if (include?.includes(id)) return true
      return showOnTheSidePanel ? showOnTheSidePanel() : !showOnTheTopFiltersBar()
    })
  }

  get columnFilters() {
    return this.filtersConfig.filter(({ showOnTheTopFiltersBar, showOnColumnHeader }) =>
      showOnColumnHeader ? showOnColumnHeader() : !showOnTheTopFiltersBar(),
    )
  }

  get activeFilters() {
    return this.filtersConfig.filter(filter => {
      return (
        this.computedRouteParams[filter.id]?.get() !== undefined &&
        this.computedRouteParams[filter.id]?.get() !== filter.defaultValue
      )
    })
  }

  get allFilterArgs() {
    return this.filtersConfig.reduce<Record<string, any>>(
      (filtersById, { id, filterOutputFormatter }) => {
        const value = this.computedRouteParams[id]?.get()
        const output = filterOutputFormatter ? filterOutputFormatter(value) : value

        if (output !== undefined) {
          return { ...filtersById, [id]: output }
        }
        return filtersById
      },
      {},
    )
  }

  get filteringArgs() {
    return this.activeFilters.reduce<Record<string, any>>(
      (filtersById, { id, filterOutputFormatter }) => {
        const value = this.computedRouteParams[id]?.get()
        const output = filterOutputFormatter ? filterOutputFormatter(value) : value
        return { ...filtersById, [id]: output }
      },
      {},
    )
  }

  get activeSidePanelFilters() {
    return this.sidePanelFilters.filter(
      filter =>
        this.computedRouteParams[filter.id]?.get() !== undefined &&
        this.computedRouteParams[filter.id]?.get() !== filter.defaultValue,
    )
  }

  get activeSidePanelFiltersCount() {
    return this.activeSidePanelFilters.length
  }

  get filtersForPreview(): FiltersForPreview[] {
    return this.filtersConfig
      .filter(
        ({ id, showOnTheAppliedFilters: showOnTheFiltersPreviewBar, showOnTheTopFiltersBar }) => {
          const value = this.computedRouteParams[id].get()
          return (
            (showOnTheFiltersPreviewBar
              ? showOnTheFiltersPreviewBar(value)
              : !showOnTheTopFiltersBar()) && value !== undefined
          )
        },
      )
      .map(({ id, label, filterPreviewFormatter, filterPreviewNoCapitalization }) => ({
        id,
        label,
        filterPreviewFormatter,
        filterPreviewNoCapitalization,
        onClear: () => this.clearFilter(id),
      }))
  }

  get sidebarFiltersCount(): number {
    return this.sidePanelFilters.length
  }

  getVisibleExtendedColumn = computedFn(() => {
    return this.extendedColumnsView
  })

  getActiveExtendedColumnKey = computedFn((columnKey?: ExtendedColumnKey) => {
    return columnKey ? this.extendedColumnsView?.includes(columnKey) : false
  })

  setFilterValue(id: string, value: any) {
    this.computedRouteParams[id]?.push(value)
  }

  applyFilters(values: { [id: string]: any }) {
    Object.keys(values).forEach(id => this.setFilterValue(id, values[id]))
  }

  clearSidebarFilters() {
    this.activeSidePanelFilters.forEach(({ id }) => this.clearFilter(id))
  }

  clearFilter(id: string) {
    this.computedRouteParams[id].push(undefined)
  }

  clearAllFilters() {
    this.activeFilters.forEach(({ id }) => this.clearFilter(id))
  }

  private updateExtendedColumns(columns: ExtendedColumnKey[]) {
    const updatedView = new Set(this.extendedColumnsView || [])

    columns.forEach(column => {
      if (updatedView.has(column)) {
        updatedView.delete(column)
      } else {
        updatedView.add(column)
      }
    })

    this.extendedColumnsView = Array.from(updatedView)
    setLocalStorageItem(EXTENDED_COLUMNS_KEY, this.extendedColumnsView)
  }

  /**
   * @deprecated use setExtendedMultipleColumnsConfig instead
   */
  setExtendedColumnsConfig(column: ExtendedColumnKey) {
    this.updateExtendedColumns([column])
  }

  setExtendedMultipleColumnsConfig(columns: ExtendedColumnKey[]) {
    this.updateExtendedColumns(columns)
  }

  getFilterValue = computedFn((id: string) => {
    return this.computedRouteParams[id].get()
  })
}
